import Script from 'next/script';
import { buildBreadcrumbSchema } from '@/lib/seo/schemas';
import type { BreadcrumbItem } from '@/lib/seo/schemas';
import { ORGANIZATION } from '@/lib/seo/constants';

interface JsonLdProps {
  id: string;
  data: Record<string, unknown> | Record<string, unknown>[];
}

export function JsonLd({ id, data }: JsonLdProps) {
  return (
    <Script
      id={id}
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}

interface ServiceSchemaProps {
  serviceName: string;
  serviceDescription: string;
  serviceUrl: string;
  serviceType?: string;
}

export function ServiceSchema({
  serviceName,
  serviceDescription,
  serviceUrl,
  serviceType = 'MedicalService',
}: ServiceSchemaProps) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': serviceType,
    name: serviceName,
    description: serviceDescription,
    provider: {
      '@type': 'MedicalOrganization',
      '@id': ORGANIZATION.id,
      name: ORGANIZATION.name,
      url: ORGANIZATION.url,
      address: {
        '@type': 'PostalAddress',
        ...ORGANIZATION.address,
      },
      telephone: ORGANIZATION.telephone,
    },
    serviceType: serviceName,
    areaServed: {
      '@type': 'Country',
      name: 'United Arab Emirates',
    },
    availableChannel: {
      '@type': 'ServiceChannel',
      serviceUrl: serviceUrl,
      servicePhone: ORGANIZATION.telephone,
    },
    medicalSpecialty: 'Pediatric Therapy',
  };

  return (
    <JsonLd
      id={`service-schema-${serviceName.toLowerCase().replace(/\s+/g, '-')}`}
      data={schema}
    />
  );
}

interface QAPageSchemaProps {
  question: string;
  answer: string;
  author?: string;
  datePublished?: string;
}

export function QAPageSchema({
  question,
  answer,
  author = 'The Speech Clinic Dubai',
  datePublished,
}: QAPageSchemaProps) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'QAPage',
    mainEntity: {
      '@type': 'Question',
      name: question,
      text: question,
      dateCreated: datePublished || new Date().toISOString(),
      author: {
        '@type': 'Organization',
        name: author,
        url: 'https://www.thespeechclinic.ae',
      },
      acceptedAnswer: {
        '@type': 'Answer',
        text: answer,
        dateCreated: datePublished || new Date().toISOString(),
        author: {
          '@type': 'Organization',
          name: author,
          url: 'https://www.thespeechclinic.ae',
        },
      },
    },
  };

  return (
    <JsonLd
      id={`qa-schema-${question.toLowerCase().replace(/\s+/g, '-').substring(0, 50)}`}
      data={schema}
    />
  );
}

interface BreadcrumbSchemaProps {
  items: BreadcrumbItem[];
  id?: string;
}

export function BreadcrumbSchema({ items, id = 'breadcrumb-schema' }: BreadcrumbSchemaProps) {
  return <JsonLd id={id} data={buildBreadcrumbSchema(items)} />;
}

interface VideoObjectSchemaProps {
  id: string;
  data: Record<string, unknown>;
}

export function VideoObjectSchema({ id, data }: VideoObjectSchemaProps) {
  return <JsonLd id={id} data={data} />;
}
